/**
 * Where a played note ends.
 *
 * A hand does not let a key go where the note ends on the page. It lifts early to reach the
 * next key, or the pedal holds the sound and the finger is long gone, or it lingers into
 * the note after. What is written is none of that: a note on a page mostly lasts until
 * something else on its staff begins. So on a staff a hand played, a note is let go at an
 * attack -- the attack on its staff nearest to where the hand let it go -- and where it
 * was let go is otherwise not taken at its word.
 *
 * Whether a hand played the staff is read from the staff. A file a program performed lets
 * its notes go where the page says, to the tick, and most of them end where another
 * begins; a hand's do so a time in four or five. So where most of a staff's notes already
 * end at an attack, or on the grid exactly, every one of them is left as it is: the rests
 * in it were meant.
 *
 * A note let go exactly on the grid was written that long and is left that long, and so is
 * a played note nothing on its staff is struck after: there is no attack to end it at.
 */

import type { ScaledNote } from "./spans";

/** The share of a staff's notes ending where another begins, from which they are taken at their word. */
const MEANT = 0.5;

/** The first of the sorted `attacks` at or after `tick`, as an index. */
function from(attacks: readonly number[], tick: number): number {
  let low = 0;
  let high = attacks.length;
  while (low < high) {
    const half = Math.floor((low + high) / 2);
    if ((attacks[half] as number) >= tick) high = half;
    else low = half + 1;
  }
  return low;
}

/** Whether the staff's notes are let go where the page would: at an attack, or on the grid. */
function meant(notes: readonly ScaledNote[], attacks: readonly number[]): boolean {
  const last = attacks[attacks.length - 1] ?? 0;
  let within = 0;
  let placed = 0;
  for (const note of notes) {
    const end = note.start + note.ticks;
    if (end > last) continue;
    within += 1;
    if (note.played !== true || attacks[from(attacks, end)] === end) placed += 1;
  }
  return placed >= MEANT * within;
}

/** One staff's notes, each played one ending at the attack nearest to where it was let go. */
export function released(notes: readonly ScaledNote[]): readonly ScaledNote[] {
  const attacks = [...new Set(notes.map((note) => note.start))].sort(
    (first, second) => first - second,
  );
  if (meant(notes, attacks)) return notes;
  return notes.map((note) => {
    if (note.played !== true) return note;
    const end = note.start + note.ticks;
    const at = from(attacks, end);
    const after = attacks[at];
    const before = attacks[at - 1];
    // Only an attack after the note's own can end it; of two as near, the later.
    const earlier = before !== undefined && before > note.start ? before : undefined;
    if (after === undefined && earlier === undefined) return note;
    const nearest =
      after === undefined || (earlier !== undefined && end - earlier < after - end)
        ? (earlier as number)
        : after;
    return { ...note, ticks: nearest - note.start };
  });
}
